import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/flexkit/Command";
import { type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent } from "@/components/podkit/popover/Popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type React from "react";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type FC } from "react";
import { Textarea, type TextareaProps } from "@/components/podkit/forms/Textarea";

export type CommandSuggestion = {
    value: string;
    label: string;
    description?: string;
    id: string;
    /** Optional group name for section headers in the popover */
    group?: string;
};

type Props = {
    suggestions?: CommandSuggestion[];
    onSuggestionSelect?: (suggestion: CommandSuggestion) => boolean;
    triggerChar?: string;
    popoverWidthOffset?: number;
    popoverSideOffset?: number;
    popoverSide?: "top" | "bottom";
    popoverAlignOffset?: number;
    /** When set, shows this message instead of suggestions when trigger char is typed */
    disabledMessage?: string;
} & TextareaProps &
    PropsWithClassName;

/**
 * Renders suggestions grouped by their `group` field.
 * Ungrouped suggestions appear first without a heading.
 * Each distinct group gets its own CommandGroup with a heading.
 */
const SuggestionGroups: FC<{
    suggestions: CommandSuggestion[];
    getItemId: (suggestion: CommandSuggestion) => string;
    onSelect: (value: string) => void;
}> = ({ suggestions, getItemId, onSelect }) => {
    const groups = useMemo(() => {
        const result: { heading?: string; items: CommandSuggestion[] }[] = [];
        let currentGroup: { heading?: string; items: CommandSuggestion[] } | undefined;

        for (const suggestion of suggestions) {
            const heading = suggestion.group;
            if (currentGroup && currentGroup.heading === heading) {
                currentGroup.items.push(suggestion);
            } else {
                currentGroup = { heading, items: [suggestion] };
                result.push(currentGroup);
            }
        }

        return result;
    }, [suggestions]);

    return (
        <>
            {groups.map((group) => (
                <CommandGroup key={group.heading ?? "__default"} heading={group.heading}>
                    {group.items.map((suggestion) => (
                        <CommandItem
                            key={suggestion.value}
                            id={getItemId(suggestion)}
                            data-index={suggestion.id}
                            value={suggestion.value}
                            onSelect={onSelect}
                            className="cursor-pointer"
                        >
                            <div className="flex w-full min-w-0 flex-col gap-0.5">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="min-w-0 flex-1 truncate font-medium">{suggestion.label}</span>
                                    <span className="shrink-0 font-mono text-xs text-content-brand">
                                        {suggestion.value}
                                    </span>
                                </div>
                                {suggestion.description && (
                                    <div className="min-w-0 truncate text-xs text-content-muted">
                                        {suggestion.description}
                                    </div>
                                )}
                            </div>
                        </CommandItem>
                    ))}
                </CommandGroup>
            ))}
        </>
    );
};

const TextareaWithSuggestions = forwardRef<HTMLTextAreaElement, Props>(
    (
        {
            suggestions = [],
            onSuggestionSelect,
            triggerChar = "/",
            className,
            value,
            onChange,
            onKeyDown,
            onBlur,
            popoverWidthOffset = 16,
            popoverSideOffset = 24,
            popoverSide = "top",
            popoverAlignOffset,
            disabledMessage,
            ...textareaProps
        },
        ref,
    ) => {
        const textareaRef = useRef<HTMLTextAreaElement>(null);
        useImperativeHandle(ref, () => textareaRef.current!, []);

        const commandRef = useRef<HTMLDivElement>(null);
        const popoverContentRef = useRef<HTMLDivElement>(null);
        const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const [open, setOpen] = useState(false);
        const [filteredSuggestions, setFilteredSuggestions] = useState<CommandSuggestion[]>([]);
        const [selectedValue, setSelectedValue] = useState<string>("");
        const [currentCommand, setCurrentCommand] = useState<{
            query: string;
            startPos: number;
            endPos: number;
        } | null>(null);
        const [dropdownPosition, setDropdownPosition] = useState<{
            width: number;
            maxHeight?: number;
        } | null>(null);
        const [announcementMessage, announce] = useState<string>("");

        // Clean up blur timeout on unmount to prevent state updates after teardown
        useEffect(() => {
            return () => {
                if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                }
            };
        }, []);

        const commandMenuId = useId();
        const getItemId = useCallback(
            (suggestion: CommandSuggestion) => {
                return `${commandMenuId}-item-${suggestion.id}`;
            },
            [commandMenuId],
        );

        // Extract current command being typed
        const getCurrentCommand = useCallback(
            (text: string, cursorPos: number) => {
                const beforeCursor = text.substring(0, cursorPos);
                const lastTriggerIndex = beforeCursor.lastIndexOf(triggerChar);

                if (lastTriggerIndex === -1) return null;

                // Check if trigger is at start of line or after whitespace
                const charBeforeTrigger = lastTriggerIndex > 0 ? beforeCursor[lastTriggerIndex - 1] : " ";
                if (charBeforeTrigger !== " " && charBeforeTrigger !== "\n") return null;

                // Extract query after trigger
                const query = beforeCursor.substring(lastTriggerIndex);

                // Check if there's a space after trigger (which would end the command)
                if (query.includes(" ", 1) || query.includes("\n", 1)) return null;

                return {
                    query,
                    startPos: lastTriggerIndex,
                    endPos: lastTriggerIndex + query.length,
                };
            },
            [triggerChar],
        );

        const updateSuggestions = useCallback(
            (text: string, cursorPos: number) => {
                const command = getCurrentCommand(text, cursorPos);
                setCurrentCommand(command);

                if (!command) {
                    setOpen(false);
                    setFilteredSuggestions([]);
                    announce("");
                    return;
                }

                // If suggestions are disabled, show the popover with the disabled message
                if (disabledMessage) {
                    setFilteredSuggestions([]);
                    setOpen(true);
                    announce(disabledMessage);
                    return;
                }

                let filtered: CommandSuggestion[];
                const searchQuery = command.query.substring(1).toLowerCase();

                if (searchQuery.length === 0) {
                    // Show all suggestions when just trigger is typed
                    filtered = suggestions;
                } else {
                    // Filter suggestions based on query
                    filtered = suggestions.filter(
                        (suggestion) =>
                            suggestion.value.toLowerCase().includes(searchQuery) ||
                            suggestion.label.toLowerCase().includes(searchQuery),
                    );
                }

                setFilteredSuggestions(filtered);

                if (filtered.length > 0) {
                    setOpen(true);

                    // Set initial selection
                    const initialSelection = filtered.find((s) => s.value === selectedValue) || filtered[0];
                    if (initialSelection.value !== selectedValue) {
                        setSelectedValue(initialSelection.value);
                    }

                    // Announce to screen readers with initial selection
                    announce(
                        `Command menu opened. ${filtered.length} command${filtered.length === 1 ? "" : "s"} available. Selected: ${initialSelection.value} ${initialSelection.label}: ${initialSelection.description || ""}`,
                    );
                } else {
                    setOpen(false);
                    announce("No commands found");
                }
            },
            [suggestions, getCurrentCommand, selectedValue, disabledMessage],
        );

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.target.value;
                const cursorPos = e.target.selectionStart;

                onChange?.(e);
                updateSuggestions(newValue, cursorPos);
            },
            [onChange, updateSuggestions],
        );

        const handleSuggestionSelect = useCallback(
            (suggestionValue: string) => {
                const suggestion = filteredSuggestions.find((s) => s.value === suggestionValue);
                if (!suggestion || !textareaRef.current || !currentCommand) return;

                const textarea = textareaRef.current;
                const currentValue = textarea.value;

                let suggestionRealValue = suggestion.value;
                const dontInsertCommand = onSuggestionSelect?.(suggestion);
                if (dontInsertCommand) {
                    suggestionRealValue = "";
                }

                // Replace the current command with the selected suggestion
                const beforeCommand = currentValue.substring(0, currentCommand.startPos);
                const afterCommand = currentValue.substring(currentCommand.endPos);
                const newValue = beforeCommand + suggestionRealValue + " " + afterCommand;
                const newCursorPos = currentCommand.startPos + suggestion.value.length + 1;

                // Update textarea value
                textarea.value = newValue;
                textarea.setSelectionRange(newCursorPos, newCursorPos);

                // Create a proper change event
                const changeEvent = {
                    target: textarea,
                    currentTarget: textarea,
                } as React.ChangeEvent<HTMLTextAreaElement>;

                onChange?.(changeEvent);

                setOpen(false);
                setCurrentCommand(null);
                setFilteredSuggestions([]);
                setSelectedValue("");
                announce(`Command ${suggestion.label} inserted`);

                // Return focus to textarea
                textarea.focus();
            },
            [currentCommand, filteredSuggestions, onChange, onSuggestionSelect],
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (open && filteredSuggestions.length > 0) {
                    switch (e.key) {
                        case "ArrowDown": {
                            e.preventDefault();
                            const currentIndex = filteredSuggestions.findIndex((s) => s.value === selectedValue);
                            const nextIndex = (currentIndex + 1) % filteredSuggestions.length;
                            const nextCommand = filteredSuggestions[nextIndex];
                            setSelectedValue(nextCommand.value);
                            announce(`${nextCommand.value} ${nextCommand.label}: ${nextCommand.description || ""}`);
                            return;
                        }
                        case "ArrowUp": {
                            e.preventDefault();
                            const currentIndex = filteredSuggestions.findIndex((s) => s.value === selectedValue);
                            const prevIndex = currentIndex === 0 ? filteredSuggestions.length - 1 : currentIndex - 1;
                            const prevCommand = filteredSuggestions[prevIndex];
                            setSelectedValue(prevCommand.value);
                            announce(`${prevCommand.value} ${prevCommand.label}: ${prevCommand.description || ""}`);
                            return;
                        }
                        case "Enter":
                        case "Tab": {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSuggestionSelect(selectedValue);
                            return;
                        }
                        case "Escape": {
                            e.preventDefault();
                            setOpen(false);
                            setCurrentCommand(null);
                            setFilteredSuggestions([]);
                            setSelectedValue("");
                            announce("Command menu closed");
                            return;
                        }
                        default:
                            break;
                    }
                }

                // Update suggestions on cursor movement
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    setTimeout(() => {
                        const textarea = e.target as HTMLTextAreaElement;
                        updateSuggestions(textarea.value, textarea.selectionStart);
                    }, 0);
                }

                onKeyDown?.(e);
            },
            [open, filteredSuggestions, selectedValue, handleSuggestionSelect, updateSuggestions, onKeyDown],
        );

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLTextAreaElement>) => {
                const textarea = e.target as HTMLTextAreaElement;
                setTimeout(() => {
                    updateSuggestions(textarea.value, textarea.selectionStart);
                }, 0);
            },
            [updateSuggestions],
        );

        const handleBlur = useCallback(
            (e: React.FocusEvent<HTMLTextAreaElement>) => {
                // Check if focus is moving to the command menu
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget && commandRef.current?.contains(relatedTarget)) {
                    // Focus is moving to command menu, don't close
                    return;
                }

                // Delay dismissing suggestions to allow click events to complete
                blurTimeoutRef.current = setTimeout(() => {
                    setOpen(false);
                    setCurrentCommand(null);
                    setFilteredSuggestions([]);
                    setSelectedValue("");
                }, 150);
                onBlur?.(e);
            },
            [onBlur],
        );

        // Calculate available space and update dropdown position
        const calculateDropdownPosition = useCallback(() => {
            if (!textareaRef.current) return;

            const textarea = textareaRef.current;
            const rect = textarea.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const collisionPadding = 16;
            const sideOffset = popoverSideOffset;

            // Calculate available space for both top and bottom to handle Radix UI's automatic flipping
            const spaceAbove = rect.top - collisionPadding - sideOffset;
            const spaceBelow = viewportHeight - rect.bottom - collisionPadding - sideOffset;

            // Use the larger available space (Radix will flip if needed)
            const availableHeight = Math.max(spaceAbove, spaceBelow);

            // Ensure minimum height and respect viewport constraints
            // Reserve at least 2rem (32px) for margins and padding
            const minHeight = 120; // Minimum height for usability
            const maxAllowedHeight = Math.max(
                minHeight,
                Math.min(availableHeight, viewportHeight - 4 * 16), // 4rem reserved for margins
            );

            setDropdownPosition({
                width: rect.width + popoverWidthOffset,
                maxHeight: maxAllowedHeight,
            });
        }, [popoverSideOffset, popoverWidthOffset]);

        // Update dropdown position on resize and when popover opens
        useEffect(() => {
            if (!open || !textareaRef.current) return;

            calculateDropdownPosition();

            const textarea = textareaRef.current;
            const resizeObserver = new ResizeObserver(() => {
                calculateDropdownPosition();
            });

            const handleResize = () => {
                calculateDropdownPosition();
            };

            resizeObserver.observe(textarea);
            window.addEventListener("resize", handleResize);
            window.addEventListener("scroll", handleResize, true);

            return () => {
                resizeObserver.disconnect();
                window.removeEventListener("resize", handleResize);
                window.removeEventListener("scroll", handleResize, true);
            };
        }, [open, calculateDropdownPosition]);

        const activeDescendant = useMemo(() => {
            if (!open || !selectedValue) return undefined;
            const selected = filteredSuggestions.find((s) => s.value === selectedValue);

            return selected ? getItemId(selected) : undefined;
        }, [open, selectedValue, filteredSuggestions, getItemId]);

        useEffect(() => {
            if (!open || !selectedValue) return;

            requestAnimationFrame(() => {
                const selectedElement = document.querySelector(`[data-value="${selectedValue}"]`);
                if (selectedElement) {
                    selectedElement.scrollIntoView({
                        block: "nearest",
                        behavior: "auto",
                        inline: "nearest",
                    });
                }
            });
        }, [selectedValue, open]);

        return (
            <div className="relative">
                {/* mx-[1px] ensures there's a tiny bit of spacing between the scrollbar and the container */}
                <div role="combobox" aria-expanded={open} aria-haspopup="listbox" className="mx-[1px]">
                    <Textarea
                        ref={textareaRef}
                        className={className}
                        value={value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onClick={handleClick}
                        onBlur={handleBlur}
                        aria-controls={commandMenuId}
                        aria-activedescendant={activeDescendant}
                        aria-autocomplete="list"
                        {...textareaProps}
                        data-tracking-id="suggestion-selected-textarea-with-suggestions"
                    />
                </div>

                {/* Screen reader announcements */}
                <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true">
                    {announcementMessage}
                </div>

                {/* Disabled message popover - shown when disabledMessage is set and trigger is detected */}
                {disabledMessage && open && filteredSuggestions.length === 0 && (
                    <Popover open={open} modal={false}>
                        <PopoverPrimitive.Anchor asChild virtualRef={textareaRef} />
                        <PopoverContent
                            ref={popoverContentRef}
                            side={popoverSide}
                            sideOffset={popoverSideOffset}
                            align="start"
                            alignOffset={popoverAlignOffset}
                            collisionPadding={16}
                            className="m-0 flex flex-col p-3"
                            style={{
                                width: dropdownPosition?.width,
                                minWidth: "min(100vw - 2rem, 280px)",
                                maxWidth: "calc(100vw - 2rem)",
                            }}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                            <p className="text-sm text-content-secondary">{disabledMessage}</p>
                        </PopoverContent>
                    </Popover>
                )}

                {/* Suggestions popover - shown when there are suggestions to display */}
                {filteredSuggestions.length > 0 && (
                    <Popover open={open} modal={false}>
                        <PopoverPrimitive.Anchor asChild virtualRef={textareaRef} />
                        <PopoverContent
                            ref={popoverContentRef}
                            side={popoverSide}
                            sideOffset={popoverSideOffset}
                            align="start"
                            alignOffset={popoverAlignOffset}
                            collisionPadding={16}
                            className="m-0 flex flex-col border-0 p-0"
                            style={{
                                width: dropdownPosition?.width,
                                minWidth: "min(100vw - 2rem, 280px)",
                                maxWidth: "calc(100vw - 2rem)",
                                maxHeight: dropdownPosition?.maxHeight
                                    ? `${dropdownPosition.maxHeight}px`
                                    : "min(16rem, calc(100vh - 8rem))",
                            }}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                            <Command
                                id={commandMenuId}
                                className="flex min-h-0 flex-1 flex-col overflow-hidden"
                                value={selectedValue}
                                onValueChange={setSelectedValue}
                            >
                                <CommandList className="min-h-0 flex-1 overflow-y-auto">
                                    <CommandEmpty>No commands found</CommandEmpty>
                                    <SuggestionGroups
                                        suggestions={filteredSuggestions}
                                        getItemId={getItemId}
                                        onSelect={handleSuggestionSelect}
                                    />
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        );
    },
);
TextareaWithSuggestions.displayName = "TextareaWithSuggestions";

export default TextareaWithSuggestions;
